import {
  GAME, HOST, PROTOCOL_VERSION, gameMessageProblem, isGameId, isHostMessage, isText, safeOrigin,
  type ActionFields, type ActionPayload, type Authorization, type CheckpointInput, type CheckpointPayload,
  type Envelope, type OutcomePayload
} from './wire-hitz-v1'
import { actionHash, checkpointHash } from './hash'
import { validateEvent, EventValidationError } from './events'
import { originAllowed } from './origin'

export * from './wire-hitz-v1'
export * from './events'
export { actionHash, checkpointHash, sha256Hex } from './hash'
export { originAllowed } from './origin'

/** Local analytics event (hitz.track). Never sent to the Hitz host: the wire v1 has no free-form events. */
export interface LocalEvent { name: string; data?: Record<string, unknown>; at: number }

/** Extra analytics destination (e.g. GameAnalytics, PostHog). The platform remains the source of truth. */
export interface AnalyticsAdapter { name: string; track(e: LocalEvent): void }

export interface SessionInfo {
  sessionId: string
  standalone: boolean
  /** Level titles: the host's informative starting level from `host.initialize` (the backend still validates the request). */
  currentLevel?: number
}

export class AttemptRejected extends Error {
  constructor(readonly levelId: string, readonly reasonCode: string) { super(`attempt rejected: ${reasonCode}`) }
}

/**
 * The game cannot run here: no Hitz App host (direct URL of a published build) or a host origin outside the allow-list.
 * Show a minimal "open this game from the Hitz App" screen; never start the engine, the simulation or audio (spec §2.3).
 */
export class HitzBlocked extends Error {
  constructor(readonly reason: 'no_host' | 'origin_not_allowed', detail?: string) {
    super(reason === 'no_host' ? `open this game from the Hitz App${detail ? ` (${detail})` : ''}` : `host origin not allowed: ${detail ?? 'unknown'}`)
  }
}

export interface HitzOptions {
  gameId: string
  version: string
  /** Announced in `game.ready`. Informative only. Default: `actions`, `checkpoints`, `seeded-<gameId>`. */
  capabilities?: string[]
  /** `rulesVersion` of the local rules. An authorization with another value is accepted only if it is a published ULID. */
  rulesVersion?: string
  /** Authorizable `levelId`s. Include `'next'` when the host resolves the level (numeric ids are then accepted). */
  levels?: readonly string[]
  /** The title uses move/time limits (positive numbers). Default false: an authorization with limits is refused. */
  allowLimits?: boolean
  /** Report `pause`/`resume` automatically when the tab is hidden/shown. Default false: pause is a decision of the title. */
  autoPause?: boolean
  customEvents?: readonly string[]
  /** Accepted host origins (config/env/<environment>.json). The host must also be the iframe's parent origin. */
  platformOrigins: readonly string[]
  adapters?: AnalyticsAdapter[]
  /**
   * Standalone play without a host is the local-development exception of the standard: allowed only when this is true
   * (`import.meta.env.DEV` on a loopback hostname, outside any iframe). A published build opened by its direct URL gets
   * `init()` rejected with `HitzBlocked('no_host')` and must stay on a minimal waiting/blocked screen.
   */
  localDevelopment?: boolean
  /** Delay before the local session opens in local development. */
  standaloneAfterMs?: number
  /**
   * Terminal `boardState.status` that `complete()` accepts: `won` for finite/level titles, `game_over` for infinite ones
   * (`COMPLETED_STATUS` in release.js). A completion with another status is misuse and is not sent.
   */
  completedStatus?: string
  /**
   * Every checkpoint's `randomState` must declare `deterministic: false` (decision of the standard, 2026-11: the seed is
   * recorded, reproduction is not claimed). Default true; only the conformance test against the old production client
   * turns it off.
   */
  checkRandomState?: boolean
  /** How long `requestAttempt` waits for the host's answer (default 5 min: the host may show a stake modal). */
  attemptTimeoutMs?: number
  /** Dev: throw on invalid events and SDK misuse. Prod: drop and warn in the console. */
  strict?: boolean
  /** Log every message to the console (enable with `?hitz_debug=1` or in local dev). */
  debug?: boolean
  /** For tests: window, message target, parent origin, clock and id source. */
  win?: Window
  target?: Window | null
  parentOrigin?: string
  now?: () => number
  uuid?: () => string
}

type AttemptListener = (attemptId: string) => void
const MAX_REMEMBERED = 256
const parseJson = (s: string): Record<string, unknown> | null => {
  try { const v = JSON.parse(s); return typeof v === 'object' && v !== null && !Array.isArray(v) ? v : null } catch { return null }
}
/** `randomState` is a JSON object whose `deterministic` is exactly false. */
const declaresNonDeterministic = (randomState: string) => parseJson(randomState)?.deterministic === false
/** `boardState.status`, when the board is a JSON object (the convention of the standard). */
const statusOf = (boardState: string) => { const s = parseJson(boardState)?.status; return typeof s === 'string' ? s : undefined }
/** Immutable id of a rules publication made by the platform (ULID). */
const PUBLISHED_RULES = /^[0-9A-HJKMNP-TV-Z]{26}$/

/**
 * Bridge between the game (iframe) and the Hitz App, speaking the Hitz wire v1.
 * Lifecycle: init() → requestAttempt(level) → start() → action()… → complete()/fail() → host finalizes → next attempt.
 * No timers, ticks or heartbeats: the platform detects abandonment on its side.
 */
export class Hitz {
  session: SessionInfo | null = null
  /** Authorization of the active attempt (null between attempts). */
  attempt: Authorization | null = null
  private nonce: string | null = null
  private hostOrigin: string | null = null
  private pending: { levelId: string; resolve(a: Authorization): void; reject(e: Error): void; timer: ReturnType<typeof setTimeout> } | null = null
  private started = false
  private startedAt = 0
  private outcomeSent = false
  private suspended = false
  private sequence = 0
  private lastActionHash = ''
  private lastCheckpointHash = ''
  private closed = new Set<string>()
  private seen = new Set<string>()
  private outbox = new Map<string, Envelope>()
  private readyResolvers: Array<(s: SessionInfo) => void> = []
  private finalizedFns: AttemptListener[] = []
  private resumedFns: AttemptListener[] = []

  constructor(private o: HitzOptions) {
    if (!isGameId(o.gameId)) throw new Error(`invalid gameId "${o.gameId}" (lowercase slug)`)
    if (!isText(o.version, 32)) throw new Error(`invalid version "${o.version}"`)
  }

  private get win(): Window { return this.o.win ?? window }
  private get target(): Window | null {
    if (this.o.target !== undefined) return this.o.target
    return this.win.parent && this.win.parent !== this.win ? this.win.parent : null
  }
  private now() { return (this.o.now ?? Date.now)() }

  /** Origin of the page embedding the game: explicit option, then the referrer, then ancestorOrigins. Never "*". */
  private parentOrigin(): string | null {
    if (this.o.parentOrigin) return safeOrigin(this.o.parentOrigin)
    const fromUrl = (u?: string) => { try { return u ? safeOrigin(new URL(u).origin) : null } catch { return null } }
    return fromUrl(this.win.document?.referrer) ?? fromUrl(this.win.location?.ancestorOrigins?.[0])
  }

  /** Announces the game and resolves when the host's session arrives (or a local one, without a host). */
  init(): Promise<SessionInfo> {
    if (this.o.autoPause) this.win.document?.addEventListener?.('visibilitychange', this.onVisibility)
    if (this.target) {
      const origin = this.parentOrigin()
      if (!origin || !originAllowed(origin, this.o.platformOrigins)) {
        console.error('[hitz] parent origin not allowed:', origin, '— check config/env platformOrigins')
        return Promise.reject(new HitzBlocked('origin_not_allowed', origin ?? undefined))
      }
      this.hostOrigin = origin
      this.win.addEventListener('message', this.onMessage)
      this.send(GAME.ready, { capabilities: this.o.capabilities ?? ['actions', 'checkpoints', `seeded-${this.o.gameId}`] })
      setTimeout(() => {
        if (!this.session) console.error('[hitz] no host.initialize after 5 s — check platformOrigins and the host page')
      }, 5000)
    } else if (this.o.localDevelopment) {
      setTimeout(() => this.openSession(`local-${this.now().toString(36)}`, 'local-nonce-not-a-secret', true), this.o.standaloneAfterMs ?? 0)
    } else {
      // Published build opened by its direct URL: no local attempt, no engine. The platform authorizes every attempt, free ones included.
      return Promise.reject(new HitzBlocked('no_host'))
    }
    return new Promise((res) => (this.session ? res(this.session) : this.readyResolvers.push(res)))
  }

  /**
   * Asks the host to authorize an attempt at `levelId`. Gameplay must not start before it resolves.
   * Rejects with AttemptRejected (host refused) or Error (timeout, attempt already pending/active).
   */
  requestAttempt(levelId: string): Promise<Authorization> {
    if (!this.session) return Promise.reject(new Error('Hitz session is not initialized'))
    if (this.pending || this.attempt) return Promise.reject(new Error('an attempt is already pending or active'))
    if (!isText(levelId, 64)) return Promise.reject(new Error(`invalid levelId "${levelId}"`))
    return new Promise<Authorization>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending = null
        reject(new Error('attempt authorization timed out'))
      }, this.o.attemptTimeoutMs ?? 300000)
      this.pending = { levelId, resolve, reject, timer }
      if (this.session!.standalone) {
        const seed = this.win.crypto?.getRandomValues ? this.win.crypto.getRandomValues(new Uint32Array(1))[0] : 1
        this.authorize({ attemptId: `local-${this.uuid()}`, levelId, levelVersion: this.o.version, seed, rulesVersion: this.o.rulesVersion ?? `${this.o.gameId}-v1` })
      } else {
        this.send(GAME.attemptStartRequested, { levelId })
      }
    })
  }

  /**
   * Gameplay really started, after the authorization and a successful setup of the initial state: emits the mandatory
   * `start` action (sequence 1, elapsedMs 0) and then the initial checkpoint (actionSequence 1). Once per attempt.
   */
  start(initial: CheckpointInput) {
    if (!this.attempt || this.outcomeSent) return this.misuse('start() needs an active attempt')
    if (this.started) return this.misuse('start() already sent for this attempt')
    this.started = true
    this.startedAt = this.now()
    this.pushAction('start', {}, 0)
    if (initial) this.checkpoint(initial); else this.misuse('start() needs the initial checkpoint')
  }

  /** A real move of the title (semantic command, not a screen tap). Only the wire's fields are accepted. */
  action(type: string, fields: ActionFields = {}, elapsedMs?: number) {
    if (!this.canAct()) return
    if (['start', 'exit'].includes(type)) return this.misuse(`use ${type}() instead of action("${type}")`)
    this.pushAction(type, fields, elapsedMs)
  }

  /** State snapshot after the current action. Initial and terminal ones are sent by start()/complete()/fail(). */
  checkpoint(c: CheckpointInput) {
    if (!this.canAct() || !this.attempt) return
    if (this.o.checkRandomState !== false && !declaresNonDeterministic(c.randomState)) {
      return this.misuse('randomState must be a JSON object with deterministic: false (standard decision; the seed is recorded, reproduction is not claimed)')
    }
    const a = this.attempt
    const p: Omit<CheckpointPayload, 'hash'> = {
      attemptId: a.attemptId, levelId: a.levelId, levelVersion: a.levelVersion, actionSequence: this.sequence,
      boardState: c.boardState, randomState: c.randomState, movesRemaining: c.movesRemaining, score: c.score, goals: c.goals,
      ...(c.boosters === undefined ? {} : { boosters: c.boosters }), restorable: !!c.restorable
    }
    const hash = checkpointHash(p)
    if (this.send(GAME.checkpoint, { ...p, hash }, a.attemptId)) this.lastCheckpointHash = hash
  }

  /**
   * The game paused (menu open, tab hidden with `autoPause`). Sends action `pause`, the checkpoint when the contract
   * asks for one (`state`) and `attempt.suspended`. Not a promise that the platform freezes settlement or the lease.
   */
  pause(reason = 'paused', state?: CheckpointInput, elapsedMs?: number) {
    if (!this.attempt || !this.started || this.outcomeSent || this.suspended) return
    this.suspended = true
    this.pushAction('pause', {}, elapsedMs)
    if (state) this.checkpoint(state)
    this.send(GAME.attemptSuspended, { reason }, this.attempt.attemptId)
  }

  resume(elapsedMs?: number) {
    if (!this.attempt || !this.suspended || this.outcomeSent) return
    this.suspended = false
    this.pushAction('resume', {}, elapsedMs)
  }

  /**
   * Claim of a normal completion, preceded by the terminal checkpoint (it carries the score). Once per attempt.
   * Not necessarily a victory: `game_over` is the natural end of an infinite title. The terminal `boardState.status` must
   * equal `completedStatus` when the title declares one; a give-up is never reclassified as a completion.
   */
  complete(terminal: CheckpointInput) {
    const want = this.o.completedStatus
    if (want && terminal && this.started && statusOf(terminal.boardState) !== want) {
      return this.misuse(`complete() needs boardState.status "${want}" (got "${statusOf(terminal.boardState)}"); use fail() for defeat or quit`)
    }
    this.outcome(GAME.attemptCompleted, terminal)
  }
  /** Claim of a loss, preceded by the terminal checkpoint. Once per attempt. */
  fail(terminal: CheckpointInput) { this.outcome(GAME.attemptFailed, terminal) }

  /**
   * Exit model A, host-controlled: asks the host to close, no outcome. Cancelling at the host keeps the match.
   * Never turn tab close or reload into an outcome: abandonment belongs to the host/backend.
   */
  requestClose() { if (this.session) this.send(GAME.closeRequested, {}) }

  /**
   * Exit model B, explicit quit inside the game: `exit` action, terminal checkpoint (score 0 by the contract) and
   * `attempt.failed`. Use it only when the title has an unambiguous give-up command.
   */
  quit(terminal: CheckpointInput) {
    if (!this.canAct()) return
    this.pushAction('exit', {})
    this.outcome(GAME.attemptFailed, terminal)
  }

  /** Exit variant used by some titles: `exit` action, then `host.close.requested`, no outcome. */
  exit() {
    if (this.attempt && this.started && !this.outcomeSent) this.pushAction('exit', {})
    this.requestClose()
  }

  /** Asks the host for fullscreen (subject to the browser's gesture and orientation rules). */
  requestFullscreen() { if (this.session) this.send(GAME.fullscreenRequested, {}) }

  /**
   * Reports a technical error to the host (`game.error`): asset failed to load, engine crash, unexpected state. Diagnostic
   * only: it does not end the attempt (use `fail()` when the title's rules say so) and the host decides what to show.
   */
  error(code: string, detail?: string) {
    if (!this.session) return this.misuse('error() needs a session')
    this.send(GAME.error, detail === undefined ? { code } : { code, detail }, this.attempt?.attemptId)
  }

  /** Posts an already sent message again, byte-for-byte (same messageId, sequence and content). */
  resend(messageId: string): boolean {
    const m = this.outbox.get(messageId)
    if (!m) return false
    this.post(m)
    return true
  }

  /** The host closed the attempt; a new one may be requested. */
  onAttemptFinalized(fn: AttemptListener) { this.finalizedFns.push(fn) }
  onAttemptResumed(fn: AttemptListener) { this.resumedFns.push(fn) }

  /** Local analytics only (adapters + debug log). Never crashes the game in production. */
  track(name: string, data?: Record<string, unknown>) {
    try { validateEvent(name, data, this.o.customEvents ?? []) } catch (err) {
      if (this.o.strict && err instanceof EventValidationError) throw err
      console.warn('[hitz]', (err as Error).message); return
    }
    const e: LocalEvent = { name, data, at: this.now() }
    if (this.o.debug) console.debug(`[hitz] track ${name}`, data ?? '')
    for (const a of this.o.adapters ?? []) { try { a.track(e) } catch { /* adapters never crash the game */ } }
  }

  destroy() {
    this.win.removeEventListener('message', this.onMessage)
    this.win.document?.removeEventListener?.('visibilitychange', this.onVisibility)
    if (this.pending) clearTimeout(this.pending.timer)
  }

  // ------------------------------------------------------------------------------------------ internals

  private uuid() { return (this.o.uuid ?? (() => this.win.crypto.randomUUID()))() }

  private misuse(msg: string) {
    if (this.o.strict) throw new Error(`[hitz] ${msg}`)
    console.warn('[hitz]', msg)
  }

  private canAct() {
    if (!this.attempt || this.outcomeSent) { this.misuse('no active attempt'); return false }
    if (!this.started) { this.misuse('call start() before other actions'); return false }
    return true
  }

  private pushAction(type: string, fields: ActionFields, elapsedMs?: number) {
    const sequence = this.sequence + 1
    const body = { sequence, type, ...fields, elapsedMs: elapsedMs ?? Math.max(0, Math.round(this.now() - this.startedAt)) }
    const hash = actionHash(this.lastActionHash, sequence, body)
    const payload: ActionPayload = { ...body, previousHash: this.lastActionHash, hash }
    if (!this.send(GAME.action, payload, this.attempt!.attemptId)) return
    this.sequence = sequence
    this.lastActionHash = hash
  }

  private outcome(type: typeof GAME.attemptCompleted | typeof GAME.attemptFailed, terminal?: CheckpointInput) {
    if (!this.attempt) return this.misuse('no active attempt')
    if (this.outcomeSent) return // once per attempt: a second claim is ignored
    if (this.started) {
      if (terminal) this.checkpoint(terminal); else this.misuse('the terminal checkpoint is required before the outcome')
    }
    const p: OutcomePayload = {
      levelId: this.attempt.levelId, actionSequence: this.sequence, actionHash: this.lastActionHash,
      ...(this.lastCheckpointHash ? { checkpointHash: this.lastCheckpointHash } : {})
    }
    this.outcomeSent = true
    this.send(type, p, this.attempt.attemptId)
    if (this.session?.standalone) { const id = this.attempt.attemptId; queueMicrotask(() => this.finalize(id)) }
  }

  private send(messageType: string, payload: object, attemptId?: string): boolean {
    const unbound = messageType === GAME.ready
    const m: Envelope = {
      protocolVersion: PROTOCOL_VERSION, gameId: this.o.gameId, gameVersion: this.o.version,
      ...(unbound ? {} : { sessionId: this.session?.sessionId, nonce: this.nonce ?? undefined }),
      ...(attemptId ? { attemptId } : {}),
      messageId: this.uuid(), messageType, occurredAt: new Date(this.now()).toISOString(),
      payload: payload as Record<string, unknown>
    }
    const problem = gameMessageProblem(m)
    if (problem) { this.misuse(`not sending invalid ${messageType}: ${problem}`); return false }
    Object.freeze(m.payload); Object.freeze(m)
    this.outbox.set(m.messageId, m)
    if (this.outbox.size > MAX_REMEMBERED) this.outbox.delete(this.outbox.keys().next().value!)
    if (this.o.debug) console.debug(`[hitz] → ${messageType}`, payload)
    this.post(m)
    return true
  }

  private post(m: Envelope) {
    const t = this.target
    if (!t || this.session?.standalone || !this.hostOrigin) return
    t.postMessage(m, this.hostOrigin) // exact origin, never "*"
  }

  private openSession(sessionId: string, nonce: string, standalone: boolean, currentLevel?: number) {
    this.nonce = nonce
    this.session = { sessionId, standalone, ...(currentLevel === undefined ? {} : { currentLevel }) }
    if (!standalone) this.send(GAME.sessionReady, {})
    for (const r of this.readyResolvers.splice(0)) r(this.session)
  }

  /** Why this title cannot play the authorized attempt, or null (spec §3.2). */
  private authorizationProblem(a: Authorization): string | null {
    if (a.levelVersion !== this.o.version) return 'level_version'
    const levels = this.o.levels
    if (levels && !levels.includes(a.levelId) && !(levels.includes('next') && /^[1-9]\d*$/.test(a.levelId))) return 'level'
    if (this.o.rulesVersion && a.rulesVersion !== this.o.rulesVersion && !PUBLISHED_RULES.test(a.rulesVersion)) return 'rules_version'
    const limits = [a.moveLimit, a.timeLimitMs]
    if (!this.o.allowLimits && limits.some((x) => x !== undefined)) return 'limits'
    if (limits.some((x) => x !== undefined && x < 1)) return 'limits'
    return null
  }

  /** Incompatible rules: no gameplay. The attempt already exists in the platform, so it is closed with a failure. */
  private refuse(a: Authorization, reasonCode: string) {
    const p = this.pending!
    clearTimeout(p.timer)
    this.pending = null
    this.attempt = a
    this.started = false; this.outcomeSent = false; this.suspended = false
    this.sequence = 0; this.lastActionHash = ''; this.lastCheckpointHash = ''
    this.outcome(GAME.attemptFailed)
    p.reject(new AttemptRejected(a.levelId, `unsupported_${reasonCode}`))
  }

  private authorize(a: Authorization) {
    const p = this.pending!
    clearTimeout(p.timer)
    this.pending = null
    this.attempt = a
    this.started = false; this.outcomeSent = false; this.suspended = false
    this.sequence = 0; this.lastActionHash = ''; this.lastCheckpointHash = ''
    p.resolve(a)
  }

  private finalize(attemptId: string) {
    this.closed.add(attemptId)
    this.attempt = null
    this.started = false
    for (const f of this.finalizedFns) f(attemptId)
  }

  private remember(messageId: string) {
    if (this.seen.has(messageId)) return false
    this.seen.add(messageId)
    if (this.seen.size > MAX_REMEMBERED) this.seen.delete(this.seen.values().next().value!)
    return true
  }

  private onVisibility = () => {
    if (this.win.document.visibilityState === 'hidden') this.pause('hidden')
    else this.resume()
  }

  private onMessage = (ev: MessageEvent) => {
    if (ev.source !== this.target || ev.origin !== this.hostOrigin) return
    const m = ev.data
    if (!isHostMessage(m) || m.gameId !== this.o.gameId || m.gameVersion !== this.o.version) return
    if (!this.remember(m.messageId)) return // retried host message: handled once
    const p = m.payload as Record<string, unknown>
    if (m.messageType === HOST.initialize) {
      if (this.session || p.hostOrigin !== ev.origin) return
      this.openSession(m.sessionId!, m.nonce!, false, p.currentLevel as number | undefined)
      return
    }
    if (!this.session || m.sessionId !== this.session.sessionId || m.nonce !== this.nonce) return
    switch (m.messageType) {
      case HOST.attemptStarted: {
        const a = p as unknown as Authorization
        // Duplicate or late authorization: never counted twice, never reopens a closed attempt.
        if (this.attempt || this.closed.has(a.attemptId) || !this.pending) return
        const want = this.pending.levelId
        if (a.levelId !== want && !(want === 'next' && /^[1-9]\d*$/.test(a.levelId))) return
        const problem = this.authorizationProblem(a)
        if (problem) return this.refuse({ ...a }, problem)
        this.authorize({ ...a })
        return
      }
      case HOST.attemptStartRejected:
        if (this.pending && p.levelId === this.pending.levelId) {
          const pend = this.pending
          clearTimeout(pend.timer)
          this.pending = null
          pend.reject(new AttemptRejected(p.levelId as string, p.reasonCode as string))
        }
        return
      case HOST.attemptResumed:
        if (this.attempt && m.attemptId === this.attempt.attemptId) for (const f of this.resumedFns) f(m.attemptId)
        return
      case HOST.attemptFinalized:
        if (this.attempt && m.attemptId === this.attempt.attemptId && this.outcomeSent) this.finalize(m.attemptId)
        return
    }
  }
}
